import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataTable,
  DataTableSkeleton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import EmptyState from 'src/components/Base/EmptyState';
import { useAuditLog } from 'src/hooks/useAuditLog';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useMemberships } from 'src/hooks/useOrganizations';

const OrgAuditLog = React.memo(() => {
  const { t } = useTranslation();
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const active = useMemo(
    () => memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0],
    [memberships, activeOrganizationId]
  );
  const organizationId = active?.id ?? null;

  const [page, setPage] = useState(1);
  const { data: audit, isLoading } = useAuditLog(organizationId, page);

  const headers = [
    { key: 'event', header: t('orgSettings.audit.event') },
    { key: 'actor', header: t('orgSettings.audit.actor') },
    { key: 'target', header: t('orgSettings.audit.target') },
    { key: 'createdAt', header: t('orgSettings.audit.createdAt') },
  ];

  if (isLoading) {
    return <DataTableSkeleton columnCount={4} rowCount={10} headers={headers} />;
  }

  const rows =
    audit?.data.map((row) => ({
      id: row.id,
      event: t(`orgSettings.audit.events.${row.event}`, { defaultValue: row.event }),
      actor: [row.actorName, row.actorEmail].filter(Boolean).join(' · '),
      target: JSON.stringify(row.target ?? {}),
      createdAt: dayjs(row.createdAt).format('DD/MM/YYYY HH:mm'),
    })) ?? [];

  return (
    <>
      <DataTable rows={rows} headers={headers}>
        {({ rows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer title={t('orgSettings.audit.title')} description={t('orgSettings.audit.subtitle')}>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && <EmptyState />}
          </TableContainer>
        )}
      </DataTable>
      <Pagination
        backwardText={t('common.previousPage')}
        forwardText={t('common.nextPage')}
        itemsPerPageText={`${t('common.itemsPerPage')}:`}
        onChange={({ page: nextPage }) => setPage(nextPage)}
        page={page}
        pageSize={25}
        pageSizes={[25]}
        // itemRangeText, not itemText: Carbon only calls itemText when `pagesUnknown` is set, which
        // this bar never sets, so the translated text was unreachable and the range rendered
        // Carbon's English default. Same fix, and the same key, as CrudPage.
        itemRangeText={(min, max, total) => t('common.itemRange', { min, max, total })}
        pageRangeText={(_current, total) =>
          `${t('common.of')} ${total} ${total === 1 ? t('common.page') : t('common.pages')}`
        }
        totalItems={audit?.total ?? 0}
        size="lg"
      />
    </>
  );
});

export default OrgAuditLog;
